import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil, isNotEmpty, isNotNil, trim} from 'ramda';

import {
  RelativeFilterChoice,
  RelativeFilterConfigBody,
  RelativeFilterResponseBody,
} from '@dms/api/core';
import i18n from '@dms/i18n';

const intOrNull = z.union([
  z.null(),
  z.number().superRefine((val, ctx) => {
    if (!Number.isInteger(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.sourcing.relativeFilters.dialog.integerRequired'),
      });
    }
  }),
]);

/** 100% cap: feature score preset offsets, and max vehicle − for mileage/power/price %. */
const MAX_PERCENT = 100;

const numericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable();

/**
 * Mileage / power / price %: `from` is 0 or in [−100, 0]; `to` is 0 or any positive % (vehicle +).
 */
const vehicleMetricPctNumericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable()
  .superRefine((bound, ctx) => {
    if (isNil(bound)) {
      return;
    }
    const signMessage = i18n.t(
      'entity.sourcing.relativeFilters.dialog.pctVehicleOffsetSignInvalid'
    );
    const belowMaxMessage = i18n.t(
      'entity.sourcing.relativeFilters.dialog.pctVehicleBelowPercentMax'
    );
    if (isNotNil(bound.from) && bound.from !== 0) {
      if (bound.from > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: signMessage,
          path: ['from'],
        });
      } else if (bound.from < -MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: belowMaxMessage,
          path: ['from'],
        });
      }
    }
    if (isNotNil(bound.to) && bound.to !== 0 && bound.to < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: signMessage,
        path: ['to'],
      });
    }
  });

/**
 * Feature score % offsets: magnitudes must stay within 0–100 from the vehicle level.
 */
const featureScorePctNumericBoundSchema = z
  .object({
    from: intOrNull,
    to: intOrNull,
  })
  .nullable()
  .superRefine((bound, ctx) => {
    if (isNil(bound)) {
      return;
    }
    const pctRangeMessage = i18n.t('entity.sourcing.relativeFilters.dialog.pctOffsetOutOfRange');
    if (isNotNil(bound.from) && bound.from !== 0) {
      if (bound.from > 0 || bound.from < -MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: pctRangeMessage,
          path: ['from'],
        });
      }
    }
    if (isNotNil(bound.to) && bound.to !== 0) {
      if (bound.to < 0 || bound.to > MAX_PERCENT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: pctRangeMessage,
          path: ['to'],
        });
      }
    }
  });

const relativeFilterChoiceSchema = z.union([z.literal('same'), z.literal('same_better'), z.null()]);

const twoWayRelativeFilterChoiceSchema = z.union([z.literal('same'), z.null()]);

export const presetEditorFormSchema = z.object({
  name: z.string().superRefine((val, ctx) => {
    if (!isNotEmpty(trim(val))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.sourcing.relativeFilters.dialog.presetNameRequired'),
      });
    }
  }),
  year: numericBoundSchema,
  mileage: numericBoundSchema,
  mileagePct: vehicleMetricPctNumericBoundSchema,
  power: numericBoundSchema,
  powerPct: vehicleMetricPctNumericBoundSchema,
  price: numericBoundSchema,
  pricePct: vehicleMetricPctNumericBoundSchema,
  featureScore: featureScorePctNumericBoundSchema,
  trimLevel: relativeFilterChoiceSchema,
  transmission: relativeFilterChoiceSchema,
  fuel: twoWayRelativeFilterChoiceSchema,
  body: twoWayRelativeFilterChoiceSchema,
  drive: relativeFilterChoiceSchema,
  vatDeductible: relativeFilterChoiceSchema,
  sellerType: twoWayRelativeFilterChoiceSchema,
  seats: relativeFilterChoiceSchema,
  radius: z.union([
    z.null(),
    z.number().superRefine((val, ctx) => {
      if (!Number.isInteger(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.sourcing.relativeFilters.dialog.integerRequired'),
        });
        return;
      }
      if (val < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.sourcing.relativeFilters.validation.radiusPositive'),
        });
      }
    }),
  ]),
});

export type PresetEditorFormValues = z.infer<typeof presetEditorFormSchema>;

const EMPTY_CONFIG: RelativeFilterConfigBody = {
  year: null,
  mileage: null,
  mileagePct: null,
  power: null,
  powerPct: null,
  price: null,
  pricePct: null,
  featureScore: null,
  trimLevel: null,
  transmission: null,
  fuel: null,
  body: null,
  drive: null,
  vatDeductible: null,
  sellerType: null,
  seats: null,
  radius: null,
};

function twoWayRelativeChoiceFromApi(value: RelativeFilterChoice | null): 'same' | null {
  return match<RelativeFilterChoice | null, 'same' | null>(value)
    .with(null, () => null)
    .with('same', () => 'same')
    .with('same_better', () => 'same')
    .exhaustive();
}

function normalizeConfigForPresetEditor(
  config: RelativeFilterConfigBody
): Omit<PresetEditorFormValues, 'name'> {
  return {
    ...config,
    fuel: twoWayRelativeChoiceFromApi(config.fuel),
    body: twoWayRelativeChoiceFromApi(config.body),
    sellerType: twoWayRelativeChoiceFromApi(config.sellerType),
  };
}

export function buildPresetEditorDefaultValues(
  initialPreset: RelativeFilterResponseBody | null
): PresetEditorFormValues {
  return {
    name: initialPreset?.name ?? '',
    ...normalizeConfigForPresetEditor(initialPreset?.config ?? EMPTY_CONFIG),
  };
}

export function getRelativeFilterConfigFromPresetEditorForm(
  values: PresetEditorFormValues
): RelativeFilterConfigBody {
  const {name: _name, ...config} = values;
  return config;
}
